/* eslint-disable no-console, no-unused-vars */

import {AVTALA_BACKEND, EXPORT_SERVICE} from './constants'
import axios from "axios";
import EventBus from "@/eventbus";

const errorHandler = (msg, err) => EventBus.$emit('error', { message: msg, error: err })

let logoutTimer = null;

function resetLogoutTimer(minutes) {
  if (logoutTimer !== null) {
    clearTimeout(logoutTimer);
  }
  logoutTimer = setTimeout(() => {
    EventBus.$emit("logout", true);
    logoutTimer = null;
  }, minutes * 60 * 1000);
}

const NO_TENANT = '0'

export const actions = {
  API({ state, getters, dispatch }, serviceName) {
    let tenant = "";
    let prefix = "";
    if (serviceName) {
      const tenantId = getters["account/tenantId"];
      if (tenantId) {
        tenant = tenantId;
        prefix = "/" + encodeURIComponent(tenant);
      } else {
        return Promise.reject(
          new Error("API call to " + serviceName + " without tenant")
        );
      }
      prefix = prefix + ("/v1/" + serviceName);
    }
    const remoteCall = function (cb) {
      return dispatch("startLoading")
        .then(() => EventBus.$keycloak.updateToken(30))
        .then(cb)
        .finally(() => dispatch("stopLoading"));
    };
    return Promise.resolve({
      http: axios,
      tenant: tenant,
      url: prefix,
      head: (url, config) => remoteCall(() => axios.head(prefix + url, config)),
      get: (url, config) => remoteCall(() => axios.get(prefix + url, config)),
      post: (url, body, config) =>
        remoteCall(() => axios.post(prefix + url, body, config)),
      put: (url, body, config) =>
        remoteCall(() => axios.put(prefix + url, body, config)),
      delete: (url, config) =>
        remoteCall(() => axios.delete(prefix + url, config)),
    });
  },

  initApplication({ dispatch, commit, state, getters }, tenantId) {
    return new Promise(function (resolve, reject) {
      return dispatch("API")
        .then((api) =>
          api.get(
            "/" + encodeURIComponent(tenantId || NO_TENANT) + "/v1/avtala"
          )
        )
        .then((response) => {
          const data = response.data;
          const tenant = data.tenant;
          const currentTenant = getters["account/tenantId"];
          if (!tenant || currentTenant !== tenant.id) {
            commit("resetState");
          }
          commit("account/setAccounts", data.accounts || []);
          commit("account/setCurrentTenant", tenant);
          commit("account/setCurrentAccount", data.user);
          commit("account/setCurrentRoles", data.roles || []);
          commit("account/setUserSettings", data.settings || {});
          commit("account/setPermissions", data.permissions || []);
          commit("setFeatures", data.features || []);
          commit("setAssetsTenant", data.assets);
          if (tenant) {
            EventBus.$emit("application:init", data);
          }
          resolve(data);
        })
        .catch((err) => reject(err));
    });
  },

  startLoading({ commit, getters }) {
    commit("setLoading", true);
    resetLogoutTimer(getters.logoutMinutes);
  },
  stopLoading({ commit }) {
    commit("setLoading", false);
  },
  ping({ getters }) {
    resetLogoutTimer(getters.logoutMinutes);
  },
  reload({ commit }, notification) {
    // Called by websocket/notification when backend sends a reload notification.
    commit("reload", notification);
    return Promise.resolve(notification);
  },
  breadcrumbs({ getters, commit }, arr) {
    const tenant = getters["account/tenantId"];
    commit(
      "setBreadcrumbs",
      arr.map((bc) => {
        if (typeof bc.to === "string") {
          const prefix = tenant ? "/" + tenant : "";
          bc.to = prefix + bc.to;
        } else {
          const to = { ...bc.to };
          if (tenant && !to.params?.tenant) {
            if (to.params) {
              to.params.tenant = tenant;
            } else {
              to.params = { tenant };
            }
          }
          bc.to = to;
        }
        return { text: bc.text, to: bc.to };
      })
    );
  },
  history({ state, commit }, arg) {
    commit("addHistory", arg);
  },
  toggleRightDrawer({ state, commit }) {
    return commit("setRightDrawer", !state.rightDrawer);
  },
  isFavorite({ dispatch }, target) {
    return new Promise(function (resolve) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.get("/contact/favorites/" + encodeURIComponent(target))
        )
        .then((response) => resolve(response.data))
        .catch((err) => {
          errorHandler("app.error.favorite.get", err);
          resolve(false);
        });
    });
  },
  toggleFavorite({ commit, dispatch }, target) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.put("/contact/favorites/" + encodeURIComponent(target))
        )
        .then((response) => resolve(response.data))
        .catch((err) => {
          errorHandler("app.error.favorite.put", err);
          resolve(false);
        });
    });
  },

  fetchFavoriteContacts({ dispatch }) {
    return new Promise(function (resolve) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/contact/favorites"))
        .then((response) => resolve(response.data))
        .catch((err) => {
          errorHandler("app.error.favorite.contacts", err);
          resolve([]);
        });
    });
  },

  fetchTimeline({ commit, dispatch }, { view, query }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.get(
            "/contract/timeline?view=" +
              encodeURIComponent(view.id) +
              "&" +
              query
          )
        )
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  // TODOs
  fetchTodoList({ commit, dispatch }, query) {
    const uri = "/contract/todos" + (query || "");
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get(uri))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  // CALENDAR
  fetchEvents({ commit, dispatch }, query) {
    const uri = "/contract/events" + (query || "");
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get(uri))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getContactSummary({ dispatch }, contact) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.get("/contact/summary/" + encodeURIComponent(contact.id))
        )
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  fetchSubscription({ dispatch }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/subscription"))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  saveSubscription({ dispatch }, data) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.post("/subscription", data))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getBillingCatalog({ dispatch }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/billing/catalog"))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getUserImages({ dispatch }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/profile/images"))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  avtalaSearchContact({ dispatch }, query) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.post("/contact/search", query))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getContactData({ dispatch }, { id, baseUrl }) {
    return new Promise(function (resolve, reject) {
      return dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.get(
            "/contact/" +
              encodeURIComponent(id) +
              "/data?flat=true&baseUrl=" +
              encodeURI(baseUrl)
          )
        )
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getContractData({ dispatch }, { id, baseUrl }) {
    return new Promise(function (resolve, reject) {
      return dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) =>
          api.get(
            "/contract/" +
              encodeURIComponent(id) +
              "/data?flat=true&baseUrl=" +
              encodeURI(baseUrl)
          )
        )
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  getBaseTemplates({ dispatch }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/export/base-templates"))
        .then((response) => resolve(response.data.items))
        .catch((err) => reject(err));
    });
  },
  getExportTemplates({ dispatch }, scope) {
    return new Promise(function (resolve, reject) {
      dispatch("API", EXPORT_SERVICE, { root: true })
        .then((api) =>
          api.get("/" + (scope ? "?scope=" + encodeURIComponent(scope) : ""))
        )
        .then((response) => resolve(response.data.items))
        .catch((err) => reject(err));
    });
  },
  getExportTemplate({ dispatch }, { id }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", EXPORT_SERVICE, { root: true })
        .then((api) => api.get("/" + encodeURIComponent(id)))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },
  saveExportTemplate({ dispatch }, data) {
    if (data.id) {
      return new Promise(function (resolve, reject) {
        dispatch("API", EXPORT_SERVICE, { root: true })
          .then((api) => api.put("/" + encodeURIComponent(data.id), data))
          .then((response) => resolve(response.data))
          .catch((err) => reject(err));
      });
    }
    return new Promise(function (resolve, reject) {
      dispatch("API", EXPORT_SERVICE, { root: true })
        .then((api) => api.post("/", data))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },
  deleteExportTemplate({ dispatch }, { id }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", EXPORT_SERVICE, { root: true })
        .then((api) => api.delete("/" + encodeURIComponent(id)))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  exportContacts({ dispatch }, request) {
    return new Promise(function (resolve, reject) {
      return dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.post("/contact/export", request))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  checkMetadata({ dispatch }) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.get("/metadata"))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },

  fixMetadata({ dispatch }, data) {
    return new Promise(function (resolve, reject) {
      dispatch("API", AVTALA_BACKEND, { root: true })
        .then((api) => api.post("/metadata", data))
        .then((response) => resolve(response.data))
        .catch((err) => reject(err));
    });
  },
};
